// ============================================================================
// SYUT ENGAGEMENT SELECT
// ============================================================================
// Domain wrapper around SyutSelect for the Engagement master.
// Label format: "Client Name - Engagement Type - Year (CODE)"
// Searchable server-side across client name, engagement type name, year, and code
// (the backend searchTerm already matches across these fields).
//
// Defaults to excluding CLOSED engagements via `onlyOpen: true`. Override with
// `onlyOpen={false}` to show all.

import React from 'react';
import { SyutSelect } from '../SyutSelect';
import { engagementService } from 'api/services/engagementService';

export interface SyutEngagementSelectProps {
  value?: string;
  onChange: (value: string) => void;
  label?: React.ReactNode;
  error?: string;
  required?: boolean;
  isClearable?: boolean;
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
  seedOptions?: { value: string; label: string }[];
  /** If true (default), exclude engagements with status CLOSED. */
  onlyOpen?: boolean;
  includeInactive?: boolean;
  /** Rows per server query (default: 500). */
  pageSize?: number;
  disabled?: boolean;
}

export const SyutEngagementSelect: React.FC<SyutEngagementSelectProps> = ({
  value,
  onChange,
  label,
  error,
  required,
  isClearable = true,
  placeholder = 'Type 3 letters to search by client, type, or year...',
  size = 'sm',
  seedOptions,
  onlyOpen = true,
  includeInactive = false,
  pageSize = 500,
  disabled,
}) => (
  <SyutSelect
    label={label}
    value={value}
    isAsync
    loadOptions={async (input: string) => {
      const res = await engagementService.getAll({
        page: 1,
        pageSize,
        searchTerm: input || undefined,
        onlyOpen,
        includeInactive,
      });
      return res.data.map((e: any) => {
        const parts = [e.clientName, e.engagementTypeName, e.year].filter(Boolean);
        const base = parts.join(' - ');
        const label = e.code ? `${base} (${e.code})` : base;
        return { value: e.id, label };
      });
    }}
    defaultOptions
    cacheOptions={false}
    seedOptions={seedOptions}
    debounceDelay={300}
    minSearchLength={3}
    isClearable={isClearable}
    size={size}
    placeholder={placeholder}
    error={error}
    required={required}
    isDisabled={disabled}
    onChange={(val) => onChange((val as string) || '')}
  />
);

export default SyutEngagementSelect;
